/**
 * Gather the COMPLETE comparison content for each drug of a compare export
 * (Export + Share-snapshot). SPL → full section text (section-filtered);
 * CA/AU/EU/UK/HA → the document's pages + page text; FDA letters → page images
 * + text; JP → the bilingual label; else the matched excerpts.
 *
 * Runs in two places with the SAME code, so a browser export and a server
 * export hold the same content by construction:
 *  - the browser (CompareKeywordPage), through the BFF with the user's token;
 *  - the export worker, straight to the backend with a job-scoped read-only
 *    token (docs/export/SERVER_SIDE_EXPORT_PLAN.md, server-side gather).
 * Everything the page knows that the backend doesn't (the search hit per drug,
 * per-column sections, Cogni evidence, highlight colours) comes in as data.
 */

import { exactTermSource } from '../components/viewer/termMarks';
import { isEnglishExactSource } from './keywordDeepLink';
import type { JPDoc, JPFigure, JPSection } from '../components/viewer/JPBilingualSections';
import { highlightHtmlString, renderSplToHighlightedHtml } from '../components/SplContentRenderer';
import type { DrugDetail, KeywordSearchResultItem, SectionResponse, UsFdaSubmissions } from '../types';
import { currentUsFdaLabelId } from './usFdaCurrentLabel';
import { regionDisplayLabel } from '../constants/filterConstants';
import type { FDALetterSectionPagesResponse } from '../types/fdaLetter';
import type {
  BatchTextSpansResponse, PagesResponse, SectionLayoutsEnsureResponse, SectionTextResponse, TOCEntry, TOCResponse,
} from '../types/viewer';
import { stripTags, withUserHighlights, highlightBlockText, type ExportBlock, type ExportDrug, type ExportDrugPdf } from './compareExport';
import { SECTION_NUMBER_NAMES, compareSectionNumbers, isHighlightsSection } from './drugDetailUtils';
import { throwIfAborted } from './exportProgress';
import type { ColoredTerm } from './highlightColors';
import { injectFigures } from './jpFigures';
import { pageNumsForSections, sectionFamilyMatcher } from './pdfSectionPages';
import { effectiveSelection, sectionNumbersOf } from './sectionSelection';
import { exportConceptSections, expandSectionFamilies, narrowedSections, type ConceptEntry } from './universalSections';

// ─── what the page knows about each column ───────────────────────────────────

/** The search hit a column came from — enough to pick the document to export
 *  and to fall back to the matched excerpts. Built from the page's search
 *  result (hitFromResult) and sent as-is to the server for a server gather. */
export interface GatherHit {
  drug_id: number;
  /** highlights[0].source: 'label', 'fda_letter', 'sba', 'ca_pm', 'jp', … */
  source: string;
  document_id?: number | null;
  /** highlights[0].section_id — addresses an FDA letter's page set. */
  section_id?: number | null;
  brand_name?: string | null;
  generic_name?: string | null;
  region?: string | null;
  /** The matched excerpts, shown when the full document is unavailable. */
  excerpts?: ExportBlock[];
}

export interface GatherContext {
  hits: ReadonlyMap<number, GatherHit>;
  /** All query terms + their colours (not only the multi-row legend ones). */
  coloredTerms: ColoredTerm[];
  /** Per-column section selection (section_numbers to show); absent = all. */
  colSections: Readonly<Record<number, string[] | undefined>>;
  /** Per column, the active Universal Section pick(s) (what the column's own
   *  filter falls back to with no manual pick); absent = all. Resolved here
   *  against the drug's OWN region (the fetched drug), like the column does.
   *  Any colSections entry, even [] (none selected), wins. */
  colConcepts?: Readonly<Record<number, ConceptEntry[] | undefined>>;
  isCogni: boolean;
  /** Cogni: the AI evidence chunk text per drug, highlighted in the export. */
  evidence: Readonly<Record<number, string | null | undefined>>;
  /** US-Drugs@FDA is live for this user: the exported region LABEL reads
   *  "US-DailyMed"/"US-Drugs@FDA". Display only; absent = today's labels. */
  usFdaVisible?: boolean;
}

/** The viewer reads the gather makes (a subset of createViewerApi's ViewerApi,
 *  declared here so the worker doesn't pull in the browser's Firebase client). */
export interface GatherViewer {
  fetchPages: (docId: number) => Promise<PagesResponse>;
  fetchTOC: (docId: number) => Promise<TOCResponse>;
  fetchSectionText: (docId: number, ids: number[]) => Promise<SectionTextResponse>;
  fetchBatchTextSpans: (docId: number, from: number, to: number) => Promise<BatchTextSpansResponse>;
  /** Scope families only (LAYOUT_PREFIXES): queue the verified page layouts of the
   *  sections' document (enqueue) or poll their progress (enqueue=false). */
  ensureSectionLayouts?: (docId: number, ids: number[], enqueue: boolean) => Promise<SectionLayoutsEnsureResponse>;
}

/** The reads the gather makes. The browser passes its BFF clients; the worker
 *  passes createHttpGatherClient over the backend. */
export interface GatherClient {
  getDrug(id: number): Promise<DrugDetail>;
  getDrugJPSections(id: number): Promise<unknown>;
  getFDALetterSectionPages(sectionId: number): Promise<FDALetterSectionPagesResponse>;
  /** The region's documents for a drug (CA/AU/UK/EU), primary first. */
  regionDocs(region: string, id: number): Promise<{ documents: { id: number }[] }>;
  /** The viewer API for a family prefix ('/sba', '/ca_pm', …). */
  viewer(prefix: string): GatherViewer;
}

export interface GatherOptions {
  signal?: AbortSignal;
  onDrugDone?: (done: number) => void;
  /** Drugs gathered at once. */
  concurrency?: number;
  /** Text-span requests in flight per document (1 = one after another). */
  spanConcurrency?: number;
  /** Page-layout generation progress: sections no longer pending / sections
   *  waited on, across all drugs. Browser exports show it ("Formatting N of M"). */
  onLayoutProgress?: (done: number, total: number) => void;
  /** Epoch ms after which still-pending layouts stop being waited for and
   *  those sections export as v3. Server jobs only (job deadline - margin); a
   *  browser export passes nothing and waits as long as it takes. */
  layoutWaitUntil?: number;
}

// ─── shared helpers (also used by the page's columns) ────────────────────────

/** A non-US `label` match carries no regulatory doc: the region's primary
 *  document is exported instead, like the live panel's RegionalPdfColumn. */
export const REGION_DOC_SOURCE: Record<string, string> = { CA: 'ca_pm', AU: 'au_pi', UK: 'uk_pil', EU: 'eu', CH: 'ch_pdf', DE: 'de_pdf', US_FDA: 'fda_label_pdf' };
/** The backend route for each region's document list: /drugs/{id}/<path>. */
export const REGION_DOC_PATH: Record<string, string> = { CA: 'ca_pms', AU: 'au_docs', UK: 'uk_pils', EU: 'eu_docs', CH: 'ch_pdf_docs', DE: 'de_pdf_docs' };
// US_FDA has no /drugs/{id}/<path> list: its label resolves by us_fda identity
// (createHttpGatherClient.regionDocs).

/** Viewer API prefix for a regulatory source (used to pull the COMPLETE doc
 *  text). FDA letters use a different endpoint (page images keyed by
 *  section_id) → null here. */
export function viewerPrefixFor(source: string): string | null {
  if (source === 'sba') return '/sba';
  if (source === 'epar') return '/epar';
  if (source === 'ca_pm') return '/ca_pm';
  if (source.startsWith('au')) return '/au';
  if (source === 'uk_pil') return '/uk_pil';
  if (source === 'eu') return '/eu';
  if (source === 'ch_pdf') return '/ch_pdf';
  if (source === 'de_pdf') return '/de_pdf';
  if (source === 'fda_label_pdf') return '/fda_label_pdf';
  return null;
}

export function sectionsFor(drug: { sections?: SectionResponse[] } | undefined, sectionNumber: string): SectionResponse[] {
  const secs = (drug?.sections ?? []).filter(
    (s) => (s.content_text && s.content_text.trim()) || (s.content_xml && s.content_xml.trim()),
  );
  // Sort numerically (BW → HIGHLIGHTS → §1, §2, §2.1 …), identical to the
  // Drug Detail page. The API returns sections in text order (1,11,12,…,2,3),
  // so without this the column renders out of order and looks jumbled.
  secs.sort((a, b) => {
    const aH = isHighlightsSection(a);
    const bH = isHighlightsSection(b);
    if (aH && !bH) return b.section_number === 'BW' ? 1 : -1;
    if (!aH && bH) return a.section_number === 'BW' ? -1 : 1;
    if (!a.section_number && b.section_number) return 1;
    if (a.section_number && !b.section_number) return -1;
    if (!a.section_number && !b.section_number) return (a.title || '').localeCompare(b.title || '');
    const cmp = compareSectionNumbers(a.section_number, b.section_number);
    return cmp !== 0 ? cmp : (a.title || '').localeCompare(b.title || '');
  });
  if (!sectionNumber) return secs;
  return secs.filter(
    (s) => s.section_number === sectionNumber || (s.section_number ?? '').startsWith(sectionNumber + '.'),
  );
}

export function sectionLabel(num: string | null | undefined, title: string | null | undefined): string {
  const t = (title || SECTION_NUMBER_NAMES[num || ''] || '').trim().replace(/^\d+(\.\d+)*\s*[.:-]?\s*/, '');
  return num ? `§${num} ${t}`.trim() : t;
}

// The viewer API serves page text for at most 50 pages per request
// (regulatory_viewer_routes.py get_page_text_spans_batch).
export const TEXT_SPAN_PAGES_PER_REQUEST = 50;

/** Column note when a Universal Section pick matches nothing in the document. */
const NO_UNIVERSAL_SECTION_NOTE = 'This document has no section matching the selected Universal Section.';

/** Section ids per section-text request (the route's cap). */
export const SECTION_TEXT_IDS_PER_REQUEST = 200;

/** Block text for a section whose chunk text can't be trusted (a `leak`) or is missing. */
const SECTION_NOT_EXTRACTED_NOTE = 'This section’s text couldn’t be extracted cleanly from the label. See the source document.';

/**
 * One block per TOC entry from the parsed chunks (the section-text route), in
 * the entries' order. Null — the caller keeps its page output — when any batch
 * fails or the document has no chunks (unless every section is a page layout).
 * A section that is missing from the response or still reports another
 * section's heading inside its text (`leak`) becomes a note block; the other
 * sections export normally. A section with no text is a heading-only block.
 */
export async function sectionTextBlocks(
  api: GatherViewer,
  docId: number,
  entries: readonly Pick<TOCEntry, 'id' | 'section_number' | 'section_number_local' | 'title' | 'group_label'>[],
  terms: string[],
  colors: (string | undefined)[],
  englishExact = false,
): Promise<ExportBlock[] | null> {
  const byId = new Map<number, SectionTextResponse['sections'][number]>();
  try {
    for (let i = 0; i < entries.length; i += SECTION_TEXT_IDS_PER_REQUEST) {
      const ids = entries.slice(i, i + SECTION_TEXT_IDS_PER_REQUEST).map((e) => e.id);
      const resp = await api.fetchSectionText(docId, ids);
      // No chunks is fine when every returned section comes from page layouts
      // (an empty response from a chunkless document proves nothing).
      if (!resp.has_chunks && (!resp.sections.length || resp.sections.some((sec) => sec.source !== 'page_layout'))) return null;
      for (const sec of resp.sections) byId.set(sec.id, sec);
    }
  } catch {
    return null;
  }
  const blocks: ExportBlock[] = [];
  for (const e of entries) {
    const sec = byId.get(e.id);
    const heading = sectionLabel(e.section_number_local ?? e.section_number, e.title) + (e.group_label ? ` — ${e.group_label}` : '');
    if (!sec || sec.leak) {
      blocks.push({ heading, text: SECTION_NOT_EXTRACTED_NOTE });
      continue;
    }
    blocks.push(sec.html ? { heading, html: highlightHtmlString(sec.html, terms, colors, null, { blockBoundaries: true, englishExact }), ...(sec.unverified ? { unverified: true } : {}) } : { heading, text: sec.text ?? '' });
  }
  return blocks;
}

/** Text spans for pages lo..hi of a document, `pagesPerRequest` pages per call
 *  (Infinity = one call for the whole range, the pre-export-v2 behaviour),
 *  `concurrency` calls in flight. */
export async function fetchTextSpansRange(
  api: GatherViewer,
  docId: number,
  lo: number,
  hi: number,
  pagesPerRequest: number,
  concurrency = 1,
): Promise<BatchTextSpansResponse['pages']> {
  const ranges: [number, number][] = [];
  for (let from = lo; from <= hi; from += pagesPerRequest) ranges.push([from, Math.min(hi, from + pagesPerRequest - 1)]);
  const parts = await mapPool(ranges, concurrency, ([from, to]) => api.fetchBatchTextSpans(docId, from, to));
  const pages: BatchTextSpansResponse['pages'] = {};
  for (const p of parts) Object.assign(pages, p.pages);
  return pages;
}

/** Run `fn` over `items` with at most `limit` in flight; results in input
 *  order. Stops starting new items once `signal` aborts. */
async function mapPool<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
  signal?: AbortSignal,
  onDone?: (done: number) => void,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  let done = 0;
  const lane = async () => {
    while (next < items.length) {
      throwIfAborted(signal);
      const i = next++;
      out[i] = await fn(items[i]!, i);
      onDone?.(++done);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, lane));
  return out;
}

// ─── verified page layouts: ensure + wait ────────────────────────────────────

/** Families whose PDF sections can be filled from verified page layouts. */
const LAYOUT_PREFIXES: ReadonlySet<string> = new Set(['/eu', '/au', '/uk_pil', '/ca_pm', '/epar']);

/** Poll interval while page layouts are being generated. */
export const POLL_MS = 5000;

interface DeferredSelection {
  index: number;
  api: GatherViewer;
  docId: number;
  sel: Pick<TOCEntry, 'id' | 'section_number' | 'section_number_local' | 'title' | 'group_label'>[];
  /** The initial (enqueue) ensure per 200-id batch, started without waiting.
   *  Resolves to the still-pending ids; a failed or malformed response settles the document (v3). */
  initial: Promise<Set<number>>;
  pending: Set<number>;
}

const batches = <T,>(items: readonly T[]): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += SECTION_TEXT_IDS_PER_REQUEST) out.push(items.slice(i, i + SECTION_TEXT_IDS_PER_REQUEST));
  return out;
};

/** One ensure/poll round for a document, validated: still-pending ids, or an
 *  empty set (settled → v3) when any request fails or a response is malformed. */
const ensureRound = (api: GatherViewer, docId: number, ids: number[], enqueue: boolean): Promise<Set<number>> =>
  Promise.all(batches(ids).map((b) => api.ensureSectionLayouts!(docId, b, enqueue)))
    .then((resps) => {
      const pending = new Set<number>();
      for (const r of resps) {
        if (!r || !Array.isArray(r.sections)) throw new Error('malformed ensure response');
        for (const sec of r.sections) if (sec?.status === 'pending') pending.add(sec.id);
      }
      return pending;
    })
    .catch(() => new Set<number>());

const CUT = Symbol('layout wait cutoff');

/** Resolves with `p`, or CUT at `until` (epoch ms); rejects with AbortError on abort. */
function raceWait<T>(p: Promise<T>, signal: AbortSignal | undefined, until: number | undefined): Promise<T | typeof CUT> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Export cancelled', 'AbortError'));
    const done = () => { clearTimeout(t); signal?.removeEventListener('abort', onAbort); };
    const onAbort = () => { done(); reject(new DOMException('Export cancelled', 'AbortError')); };
    const t = until === undefined ? undefined : setTimeout(() => { done(); resolve(CUT); }, Math.max(0, until - Date.now()));
    signal?.addEventListener('abort', onAbort, { once: true });
    p.then((v) => { done(); resolve(v); }, (e) => { done(); reject(e); });
  });
}

/** Abortable wait; rejects with the standard AbortError. */
function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return raceWait(new Promise<void>((r) => setTimeout(r, ms)), signal, undefined).then(() => undefined);
}

/** Wait until no deferred document has a pending section: await every initial
 *  ensure (all already in flight), then poll all still-pending documents
 *  together every POLL_MS. A failed or malformed ensure/poll settles that
 *  document (v3). Every request is raced against abort and `layoutWaitUntil`
 *  (cutoff: remaining documents export via v3). */
async function waitForLayouts(
  deferred: DeferredSelection[],
  opts: Pick<GatherOptions, 'signal' | 'onLayoutProgress' | 'layoutWaitUntil'>,
): Promise<void> {
  const waitUntil = opts.layoutWaitUntil;
  const total = deferred.reduce((n, d) => n + d.sel.length, 0);
  const report = () => {
    const pending = deferred.reduce((n, d) => n + d.pending.size, 0);
    opts.onLayoutProgress?.(total - pending, total);
  };
  const initial = await raceWait(Promise.all(deferred.map((d) => d.initial)), opts.signal, waitUntil);
  if (initial === CUT) return;
  deferred.forEach((d, i) => { d.pending = initial[i]!; });
  report();
  while (deferred.some((d) => d.pending.size)) {
    if (waitUntil !== undefined && Date.now() >= waitUntil) return;
    await sleepUnlessAborted(POLL_MS, opts.signal);
    if (waitUntil !== undefined && Date.now() >= waitUntil) return;
    const live = deferred.filter((d) => d.pending.size);
    const round = await raceWait(
      Promise.all(live.map((d) => ensureRound(d.api, d.docId, [...d.pending], false))),
      opts.signal,
      waitUntil,
    );
    if (round === CUT) return;
    live.forEach((d, i) => { d.pending = round[i]!; });
    report();
  }
}

// ─── the gather ──────────────────────────────────────────────────────────────

export async function gatherExportDrugs(
  ids: number[],
  ctx: GatherContext,
  client: GatherClient,
  opts: GatherOptions = {},
): Promise<ExportDrug[]> {
  const { coloredTerms, isCogni } = ctx;
  const highlightTerms = coloredTerms.map((c) => c.term);
  const highlightColors = coloredTerms.map((c) => c.color);
  const spanConcurrency = opts.spanConcurrency ?? 1;
  // Per-row keyword matchers for the snapshot page-image overlay — built per source
  // (non-global so repeated .exec() per span is safe) and shared by the
  // regulatory-PDF and FDA-letter branches so both color the same way.
  const snapshotMatchers = (englishExact: boolean) => coloredTerms.map((ct) => {
    const bare = ct.term.replace(/^\*+|\*+$/g, '').trim();
    const core = bare.split(/\s+/).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
    const left = ct.term.startsWith('*') ? '\\w*' : '\\b';
    const right = ct.term.endsWith('*') ? '\\w*' : '\\b';
    return { color: ct.color, re: new RegExp((englishExact && exactTermSource(ct.term)) || `${left}${core}${right}`, 'i') };
  });
  // Build ONE drug's export payload (fetch its label + render).
  // Selections on page-layout families whose ensure was started; resolved after
  // the lanes finish so no lane is held while layouts generate.
  const deferred: DeferredSelection[] = [];
  const gatherOne = async (id: number, index: number): Promise<ExportDrug> => {
    const hit = ctx.hits.get(id);
    let source = String(hit?.source || 'label');
    const englishExact = !isCogni && isEnglishExactSource(hit?.source);
    const snapMatchers = snapshotMatchers(englishExact);
    let drug: DrugDetail | null = null;
    try {
      drug = await client.getDrug(id);
    } catch {
      /* fall back to hit fields */
    }
    const region = drug?.region ?? hit?.region ?? 'US';
    const name = drug?.brand_name || hit?.brand_name || `Drug ${id}`;
    const subtitle = [drug?.generic_name, drug?.manufacturer, regionDisplayLabel(region, ctx.usFdaVisible ?? false)].filter(Boolean).join(' · ');
    const evidence = ctx.evidence[id] ?? null;
    // Non-US `label` match (keyword or Cogni): the result carries no regulatory
    // doc — resolve the region's document, exactly like the live panel does
    // (DrugColumn's isNonUsLabel → RegionalPdfColumn), so the export gets the
    // full document text and the snapshot the real (zoomable) PDF pages.
    let regionDocId: number | null = null;
    if (source === 'label' && region !== 'US' && REGION_DOC_SOURCE[region]) {
      try {
        const docs = await client.regionDocs(region, id);
        const rid = docs?.documents?.[0]?.id;
        if (rid != null) { source = REGION_DOC_SOURCE[region]!; regionDocId = rid; }
      } catch { /* keep label → section-text fallback */ }
    }
    const conceptNums = exportConceptSections(ctx.colConcepts?.[id] ?? [], region);
    // A Universal Section pick (no manual pick) exports ONLY that section: a
    // document without it exports an empty column with a note, never the whole
    // document. (The on-screen column applies the same rule.)
    const universalOnly = ctx.colSections[id] === undefined
      && (ctx.colConcepts?.[id] ?? []).some((c) => c.universal_order != null);
    const pickSections = (present: string[], isJp: boolean) => (universalOnly
      ? expandSectionFamilies(conceptNums ?? [], present)
      : narrowedSections({
        manual: ctx.colSections[id],
        conceptSections: conceptNums ?? null,
        isUniversal: true, // conceptNums already omits a JP evidence-only concept
        isJp,
        present,
      }));
    const isSPL = source === 'label' && region !== 'CA' && region !== 'AU';
    // JP (region='JP'): the label lives in the bilingual jp_sections family,
    // not `sections` / a PDF — so it needs its own branch (matched via
    // region, since keyword carries source='jp' and Cogni source='label').
    const isJpExport = region.toUpperCase() === 'JP';
    let blocks: ExportBlock[] = [];
    let note: string | undefined;
    let pdf: ExportDrugPdf | undefined;
    let jpPicked: string[] | undefined; // JP: an empty pick shows nothing, not excerpts
    const excerpts = () => (hit?.excerpts ?? []).map((b) => ({ ...b }));
    if (isJpExport) {
      // Full bilingual PMDA label → one block per section carrying BOTH the
      // English (default, highlighted: keyword <mark> or the Cogni evidence
      // block) and the Japanese original, so the hosted snapshot can offer an
      // EN/日本語 toggle and the static .html download still renders English.
      try {
        const jp = (await client.getDrugJPSections(id)) as {
          document: JPDoc | null;
          sections: JPSection[];
          figures?: JPFigure[];
        };
        const jpSections = jp?.sections ?? [];
        if (jp?.document && jpSections.length) {
          const figUrls: Record<string, string> = {};
          for (const f of jp.figures ?? []) if (f.image_url) figUrls[f.gfname] = f.image_url;
          const jpSel = pickSections(jpSections.map((s) => s.section_number), true);
          jpPicked = jpSel;
          const sel = jpSel ? new Set(jpSel) : null;
          // Cogni → blue evidence block (no yellow term box); keyword → terms.
          const evJp = isCogni ? evidence : null;
          const jpTerms = evJp ? [] : highlightTerms;
          const jpColors = evJp ? [] : highlightColors;
          blocks = jpSections // already numeric-sorted by the API
            .filter((s) => !sel || sel.has(s.section_number))
            .map((s) => {
              const enRaw = s.content_html;
              const jaRaw = s.content_html_ja || s.content_html;
              const enHtml = enRaw
                ? highlightHtmlString(injectFigures(enRaw, figUrls), jpTerms, jpColors, evJp, { englishExact })
                : '';
              const jaHtml = jaRaw ? injectFigures(jaRaw, figUrls) : '';
              const heading = sectionLabel(s.section_number, s.title);
              const heading_ja = sectionLabel(s.section_number, s.title_ja || s.title);
              return (enHtml || jaHtml)
                ? { heading, heading_ja, ...(enHtml ? { html: enHtml } : {}), ...(jaHtml ? { html_ja: jaHtml } : {}) }
                : { heading, text: '(no text content)' };
            });
        }
      } catch { /* fall through to excerpts */ }
      if (universalOnly && jpPicked?.length === 0) {
        note = NO_UNIVERSAL_SECTION_NOTE;
      } else if (!blocks.length && !(jpPicked && jpPicked.length === 0)) {
        blocks = excerpts();
        note = blocks.length
          ? 'Matched excerpts — full document text was unavailable.'
          : 'This drug couldn’t be loaded — it may have been removed.';
      }
    } else if (isSPL && drug?.sections?.length) {
      // Keep the SPL formatting (bold/bullets/tables/links) + highlight, same
      // as the on-screen renderer. Store ONLY the formatted html (not also the
      // plain-text duplicate) to keep the snapshot payload small — large
      // labels like KEYTRUDA are ~1.5 MB each. Plain text only when no XML.
      // Export the same sections the column shows (per-column selection).
      const eff = effectiveSelection(drug.sections, pickSections(sectionNumbersOf(drug.sections), false));
      blocks = sectionsFor(drug, '')
        .filter((s) => eff.has(s.section_number || `__${s.title ?? ''}`))
        .map((s) => {
          const html = s.content_xml
            ? renderSplToHighlightedHtml(
                s.content_xml, highlightTerms, highlightColors,
                // Cogni: highlight the AI evidence chunk in the snapshot too
                // (only the section containing it lights up), like the live panel.
                isCogni ? evidence : null,
                englishExact,
              )
            : undefined;
          return html
            ? { heading: sectionLabel(s.section_number, s.title), html }
            : { heading: sectionLabel(s.section_number, s.title), text: s.content_text || stripTags(s.content_xml) };
        });
      if (universalOnly && !blocks.length) note = NO_UNIVERSAL_SECTION_NOTE;
    } else if (source === 'fda_letter') {
      // FDA letters are scanned page images (public GCS bucket) + a
      // positioned text layer. Capture each page's public image URL so the
      // public snapshot shows the REAL letter pages — not a text excerpt —
      // and box the searched keyword(s) on each page using the same per-row
      // colors as the regulatory overlay. The letter's page set is addressed
      // by the highlight's section_id (same id the live FdaLetterPageViewer
      // uses), not document_id.
      const sectionId = hit?.section_id ?? null;
      if (sectionId && sectionId > 0) {
        try {
          const pagesResp = await client.getFDALetterSectionPages(sectionId);
          const pdfPages: ExportDrugPdf['pages'] = [];
          const marks: NonNullable<ExportDrugPdf['marks']> = [];
          const textBlocks: ExportBlock[] = [];
          for (const pg of pagesResp.pages) {
            // Page text (for the HTML/XLS export, which renders blocks not images).
            const pageText = (
              (pg.text_spans ?? []).map((s) => s.text).join(' ') || pg.extracted_text || ''
            ).replace(/\s+/g, ' ').trim();
            if (pageText) textBlocks.push({ heading: `Page ${pg.page_num + 1}`, text: pageText });
            // Use the PUBLIC GCS URL (bucket has allUsers:objectViewer) so the
            // login-free snapshot loads the page image directly — same pattern
            // as the regulatory PDFs, no base64 inlining needed.
            if (pg.image_public_url) {
              pdfPages.push({
                page_num: pg.page_num,
                image_url: pg.image_public_url,
                width: pg.image_width_px,
                height: pg.image_height_px,
              });
            }
            // Keyword match boxes. FDA bboxes are PDF POINTS, so normalise by
            // pdf_width_pt/pdf_height_pt to the 0-1 fractions SnapMarks expects.
            // Prefer per-line spans (pixel-accurate) when present, else the
            // block span with a char-ratio x estimate (same as regulatory).
            if (pg.pdf_width_pt && pg.pdf_height_pt && snapMatchers.length) {
              const pw = pg.pdf_width_pt;
              const ph = pg.pdf_height_pt;
              for (const span of pg.text_spans ?? []) {
                const units = span.lines && span.lines.length ? span.lines : [span];
                for (const u of units) {
                  const txt = u.text || '';
                  for (const m of snapMatchers) {
                    const hitM = m.re.exec(txt);
                    if (hitM) {
                      const len = txt.length || 1;
                      const ux0 = u.bbox_x0 / pw;
                      const ux1 = u.bbox_x1 / pw;
                      marks.push({
                        page: pg.page_num,
                        x0: ux0 + (hitM.index / len) * (ux1 - ux0),
                        y0: u.bbox_y0 / ph,
                        x1: ux0 + ((hitM.index + hitM[0].length) / len) * (ux1 - ux0),
                        y1: u.bbox_y1 / ph,
                        color: m.color || '#fde047',
                      });
                      break;
                    }
                  }
                }
              }
            }
          }
          if (pdfPages.length) pdf = { toc: [], pages: pdfPages, ...(marks.length ? { marks } : {}) };
          blocks = textBlocks;
        } catch { /* fall through to excerpts */ }
      }
      if (!blocks.length && !pdf) {
        blocks = excerpts();
        note = blocks.length
          ? 'Matched excerpts — letter pages were unavailable.'
          : 'This drug couldn’t be loaded — it may have been removed.';
      }
    } else {
      const docId = hit?.document_id ?? regionDocId;
      const prefix = viewerPrefixFor(source);
      const api = prefix ? client.viewer(prefix) : null;
      let keep: Set<number> | null = null; // pages of the selected sections
      // A PDF section selection whose pages/TOC fetch fails must not fail open
      // into the whole document. (No viewer at all — e.g. a US SPL with no
      // sections — keeps the usual excerpt fallback.)
      // A defined manual pick wins ([] = every page, concept ignored).
      const manualPick = ctx.colSections[id];
      const wantSel = manualPick !== undefined ? manualPick.length > 0 : universalOnly || !!conceptNums?.length;
      let selUnresolved = false;
      if (api && docId != null) {
        try {
          const pagesResp = await api.fetchPages(docId);
          const manualSel = ctx.colSections[id]; // [] = none ticked: the screen shows every page
          let colSel = manualSel;
          let rawToc: TOCEntry[] = [];
          // Capture the actual PDF pages (public GCS image URLs) + TOC so the
          // snapshot can render the real, zoomable document — not just text.
          let pdfPages = pagesResp.pages
            .filter((p) => p.image_url)
            .map((p) => ({ page_num: p.page_num, image_url: p.image_url!, width: p.width, height: p.height }));
          if (pdfPages.length || manualSel?.length || conceptNums?.length || universalOnly) {
            let toc: ExportDrugPdf['toc'] = [];
            try {
              // Carry the FULL TOC hierarchy through to the snapshot (id /
              // level / parent_section_id / group_key / is_group_header /
              // page_start) so the snapshot Contents renders the SAME
              // indented, collapsible tree as the live TOCSidebar — one
              // renderer for both. ``page_num`` is kept as a legacy flat
              // click target so pre-unification snapshots still work.
              rawToc = (await api.fetchTOC(docId)).sections;
              toc = rawToc.map((s) => {
                const sec = s as { page_start?: number | null; page_num?: number | null };
                return { ...s, page_num: sec.page_start ?? sec.page_num ?? null };
              });
            } catch {
              // TOC optional — unless a selection needs it to find its pages.
              if (wantSel) throw new Error('TOC unavailable for the section selection');
            }
            // No manual pick: the Universal Section, family-expanded against
            // THIS document's TOC numbers (the screen's pdfEff rule).
            if (!colSel && (conceptNums?.length || universalOnly)) {
              const fam = expandSectionFamilies(
                conceptNums ?? [], toc.map((e) => e.section_number).filter((n): n is string => !!n),
              );
              if (fam.length) colSel = fam;
              else if (universalOnly) keep = new Set(); // no such section: export nothing
            }
            // Only the selected sections' pages (same rule as the screen); a
            // selection that matches nothing in this document keeps every page.
            if (colSel?.length) {
              const k = pageNumsForSections(toc, pagesResp.pages, colSel);
              if (k.size) keep = k;
            }
            if (keep) pdfPages = pdfPages.filter((p) => keep!.has(p.page_num));
            if (pdfPages.length) pdf = { toc, pages: pdfPages };
          }
          const nums = pagesResp.pages.map((p) => p.page_num).filter((n) => Number.isFinite(n) && (!keep || keep.has(n)));
          const lo = nums.length ? Math.min(...nums) : 1;
          const hi = nums.length ? Math.max(...nums) : 1;
          const textPages = keep?.size === 0 ? {} : await fetchTextSpansRange(
            api, docId, lo, hi, TEXT_SPAN_PAGES_PER_REQUEST, spanConcurrency,
          );
          blocks = Object.keys(textPages)
            .filter((k) => !keep || keep.has(Number(k)))
            .sort((x, y) => Number(x) - Number(y))
            .map((k) => ({
              heading: `Page ${k}`,
              text: textPages[k]!.text_spans.map((s) => s.text).join(' ').replace(/\s+/g, ' ').trim(),
            }))
            .filter((b) => b.text);

          // Keyword match boxes for the snapshot PDF overlay — find spans in
          // the page text layer that match a query term and record the word's
          // bbox (estimated within the line) + that row's color, so the public
          // snapshot can color the keyword on the page IMAGE.
          if (pdf && snapMatchers.length) {
            const marks: NonNullable<ExportDrugPdf['marks']> = [];
            for (const [k, pg] of Object.entries(textPages)) {
              const page = Number(k);
              if (keep && !keep.has(page)) continue;
              for (const sp of pg.text_spans) {
                const txt = sp.text || '';
                for (const m of snapMatchers) {
                  const hitM = m.re.exec(txt);
                  if (hitM) {
                    const len = txt.length || 1;
                    const sx = sp.x0 + (hitM.index / len) * (sp.x1 - sp.x0);
                    const ex = sp.x0 + ((hitM.index + hitM[0].length) / len) * (sp.x1 - sp.x0);
                    marks.push({ page, x0: sx, y0: sp.y0, x1: ex, y1: sp.y1, color: m.color });
                    break;
                  }
                }
              }
            }
            if (marks.length) pdf.marks = marks;
          }
          // A selection exports just its sections' parsed text (no page images);
          // a gap (no chunks, a failed request) keeps the page output.
          if (colSel?.length) {
            const matches = sectionFamilyMatcher(colSel);
            // id <= 0 is a synthetic row (AU CMI), not a section row.
            const sel = rawToc.filter((e) => e.id > 0 && matches(e.section_number));
            let sb: ExportBlock[] | null = null;
            if (sel.length && api.ensureSectionLayouts && prefix && LAYOUT_PREFIXES.has(prefix)) {
              // Start the ensure now (never held in a lane); this drug's sections
              // are read after every document's layouts are ready (see below).
              const initial = ensureRound(api, docId, sel.map((e) => e.id), true);
              deferred.push({ index, api, docId, sel, initial, pending: new Set() });
            } else if (sel.length) {
              sb = await sectionTextBlocks(api, docId, sel, highlightTerms, highlightColors, englishExact);
            }
            if (sb) {
              blocks = sb;
              if (pdf) pdf.sectionScoped = true;
            }
          }
        } catch {
          /* fall through to excerpts — or, with a selection, to the note below */
          if (wantSel) selUnresolved = true;
        }
      }
      // No page text → the matched excerpts. Export v2: even when page images
      // were found (the file exports can't show images), so no column is blank.
      if (selUnresolved) {
        blocks = [];
        pdf = undefined;
        note = 'The selected sections couldn’t be loaded — export again to retry.';
      } else if (keep?.size === 0) {
        note = NO_UNIVERSAL_SECTION_NOTE;
      } else if (keep && !blocks.length) {
        // A resolved selection never falls back to the search excerpts (they
        // may belong to another section).
        note = 'The selected sections have no extracted text.';
      } else if (!blocks.length) {
        blocks = excerpts();
        note = blocks.length
          ? 'Matched excerpts — full document text was unavailable.'
          : 'This drug couldn’t be loaded — it may have been removed.';
      }
    }
    // getDrug failed (drug === null) AND no PDF pages were fetched → the drug
    // record is gone (stale keyword posting → deleted drug_id); any `blocks`
    // here are stale search-hit excerpts, not authoritative current content.
    const unreachable = drug === null && !pdf;
    return { name, subtitle, note, blocks, ...(pdf ? { pdf } : {}), ...(unreachable ? { unreachable: true } : {}) };
  };
  // Gather drugs concurrently, capped so a large export doesn't fire N big
  // fetches at once. Order is preserved.
  const results = await mapPool(ids, opts.concurrency ?? 4, gatherOne, opts.signal, opts.onDrugDone);
  if (deferred.length) {
    await waitForLayouts(deferred, opts);
    await Promise.all(deferred.map(async (d) => {
      throwIfAborted(opts.signal);
      const sb = await sectionTextBlocks(d.api, d.docId, d.sel, highlightTerms, highlightColors, !isCogni && isEnglishExactSource(ctx.hits.get(ids[d.index]!)?.source));
      if (!sb) return;
      const drug = results[d.index]!;
      results[d.index] = {
        ...drug,
        blocks: sb,
        note: undefined,
        ...(drug.pdf ? { pdf: { ...drug.pdf, sectionScoped: true } } : {}),
      };
    }));
  }
  return results.map((drug, index) => {
    if (isCogni || !isEnglishExactSource(ctx.hits.get(ids[index]!)?.source)) return drug;
    return { ...drug, blocks: drug.blocks.map((b) => b.html || b.text == null ? b : {
      ...b, html: highlightBlockText(b.text, '', coloredTerms, true).replace(/\n/g, '<br>'),
    }) };
  });
}

/** The user's own highlights (manual phrases per drug) on top of the gathered
 *  content — the last step of every export gather. */
export function applyUserHighlights(
  drugs: ExportDrug[],
  ids: number[],
  manual: Readonly<Record<number | string, string[] | undefined>>,
  keyword: string,
  exportTerms: ColoredTerm[] | undefined,
): ExportDrug[] {
  return drugs.map((drug, i) => withUserHighlights(drug, manual[ids[i]!] ?? [], keyword, exportTerms));
}

// ─── the worker's client: plain GETs on the backend's routes ─────────────────

/** A GatherClient over the backend's own routes (the paths the BFF forwards
 *  to, minus the /api prefix). `get`/`post` do the HTTP call and return the JSON;
 * without `post` no page layouts are requested (v3 only). */
export function createHttpGatherClient(
  get: <T>(path: string) => Promise<T>,
  post?: <T>(path: string, body: unknown) => Promise<T>,
): GatherClient {
  const viewers = new Map<string, GatherViewer>();
  return {
    getDrug: (id) => get(`/drugs/${id}`),
    getDrugJPSections: (id) => get(`/drugs/${id}/jp-sections`),
    getFDALetterSectionPages: (sectionId) => get(`/fda-letters/sections/${sectionId}/pages`),
    regionDocs: (region, id) => {
      if (region === 'US_FDA') {
        return get<UsFdaSubmissions>(`/drugs/${id}/us_fda_submissions`).then((subs) => {
          const labelId = currentUsFdaLabelId(subs);
          return { documents: labelId == null ? [] : [{ id: labelId }] };
        });
      }
      const path = REGION_DOC_PATH[region];
      return path ? get(`/drugs/${id}/${path}`) : Promise.resolve({ documents: [] });
    },
    viewer: (prefix) => {
      let v = viewers.get(prefix);
      if (!v) {
        v = {
          fetchPages: (docId) => get(`${prefix}/documents/${docId}/pages`),
          fetchTOC: (docId) => get(`${prefix}/documents/${docId}/toc`),
          fetchSectionText: (docId, ids) => get(`${prefix}/documents/${docId}/section-text?ids=${ids.join(',')}`),
          fetchBatchTextSpans: (docId, from, to) => get(`${prefix}/documents/${docId}/pages/text-spans?from=${from}&to=${to}`),
          ...(post ? {
            ensureSectionLayouts: (docId: number, ids: number[], enqueue: boolean) =>
              post<SectionLayoutsEnsureResponse>(`${prefix}/documents/${docId}/section-layouts/ensure`, { ids, enqueue }),
          } : {}),
        };
        viewers.set(prefix, v);
      }
      return v;
    },
  };
}

/** The GatherHit for a column, from the page's keyword/Cogni search result. */
export function hitFromResult(id: number, result: KeywordSearchResultItem | null | undefined, keywordMode = false): GatherHit {
  const h = result?.highlights?.[0];
  return {
    drug_id: id,
    source: String(h?.source ?? (keywordMode ? '' : 'label')),
    document_id: h?.document_id ?? null,
    section_id: h?.section_id ?? null,
    brand_name: result?.brand_name ?? null,
    generic_name: result?.generic_name ?? null,
    region: result?.region ?? null,
    excerpts: (result?.highlights ?? []).map((x) => ({
      heading: x.section_title || x.section_number || '',
      text: stripTags(x.headline),
    })),
  };
}
